import { useState, useEffect, useCallback } from 'react';
import { apiJson, apiFetch } from '../../lib/api';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";

interface UserEntry {
  github_id: string;
  github_login: string;
  email: string | null;
  avatar_url: string | null;
  display_name: string;
  last_seen_at: string;
  is_global_admin: boolean;
}

export function UserManager() {
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(() => {
    apiJson<{ users: UserEntry[] }>('/api/admin/users')
      .then(d => {
        setUsers(d.users || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleAdmin = async (userId: string, currentAdmin: boolean) => {
    await apiFetch(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_global_admin: !currentAdmin }),
    });
    loadUsers();
  };

  if (loading) {
    return <div style={{ color: '#6272a4', fontSize: 13, padding: 24, fontFamily: FONT }}>Loading users...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 11, color: '#6272a4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {users.length} registered user{users.length !== 1 ? 's' : ''}
      </div>

      <div style={{
        background: '#1e1e2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Seen</th>
              <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 10, color: '#6272a4', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Admin</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr
                key={u.github_id}
                style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'all 0.15s ease' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {u.avatar_url ? (
                      <img src={u.avatar_url} width={28} height={28} style={{ borderRadius: '50%' }} />
                    ) : (
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(80,250,123,0.12)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, color: '#50fa7b', fontFamily: FONT,
                      }}>
                        {u.github_login[0]?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 13, color: '#f8f8f2', fontWeight: 500 }}>
                        {u.display_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#6272a4' }}>@{u.github_login}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '10px 16px', fontSize: 11, color: '#a8b2b8' }}>
                  {u.email || <span style={{ color: '#6272a4' }}>-</span>}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  {u.is_global_admin && (
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 4,
                      background: 'rgba(189,147,249,0.1)', border: '1px solid rgba(189,147,249,0.25)', color: '#bd93f9',
                      textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT,
                    }}>
                      admin
                    </span>
                  )}
                </td>
                <td style={{ padding: '10px 16px', fontSize: 11, color: '#6272a4' }}>
                  {new Date(u.last_seen_at).toLocaleString()}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <button
                    onClick={() => toggleAdmin(u.github_id, u.is_global_admin)}
                    style={{
                      padding: '4px 12px',
                      background: u.is_global_admin ? 'rgba(189,147,249,0.1)' : 'transparent',
                      border: `1px solid ${u.is_global_admin ? 'rgba(189,147,249,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 4,
                      color: u.is_global_admin ? '#bd93f9' : '#6272a4',
                      fontSize: 11,
                      fontFamily: FONT,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {u.is_global_admin ? 'Revoke Admin' : 'Make Admin'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6272a4', fontSize: 13, fontFamily: FONT }}>
            No users have signed in yet.
          </div>
        )}
      </div>
    </div>
  );
}
